import { getDefaultStore } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import { parseCommandDefinition, type TCommandDefinition } from '@/lib/slash-command-definition'

/**
 * An installed slash command: a parsed version of a kind 31992 event,
 * saved (together with the author pubkey) when the user installs it.
 */
export type TInstalledCommand = {
  pubkey: string
  d: string
  command: string
  title?: string
  description?: string
  args: {
    name: string
    type: string
    required: boolean
    label?: string
    choices: string[]
  }[]
  scopes: {
    relay?: string[]
    kind?: number[]
    tagPresence?: string[]
    tagValue?: {
      [tagName: string]: string[]
    }
    author?: string[]
  }
}

export const installedCommandsAtom = atomWithStorage<TInstalledCommand[]>(
  's:installedCommands',
  [],
  undefined,
  { getOnInit: true }
)

function commandKey(pubkey: string, d: string) {
  return `${pubkey}:${d}`
}

export function isCommandInstalled(pubkey: string, d: string): boolean {
  const store = getDefaultStore()
  const key = commandKey(pubkey, d)
  return store.get(installedCommandsAtom).some((c) => commandKey(c.pubkey, c.d) === key)
}

export function installCommand(pubkey: string, tags: string[][]) {
  const store = getDefaultStore()
  const definition = parseCommandDefinition(tags)
  if (!definition.command) return

  store.set(installedCommandsAtom, (prev) => {
    const key = commandKey(pubkey, definition.d)
    const next = prev.filter((c) => commandKey(c.pubkey, c.d) !== key)

    const scopes: TInstalledCommand['scopes'] = {}
    for (const scope of definition.scopes) {
      if (scope.startsWith('kind:')) {
        const kind = Number(scope.slice('kind:'.length))
        if (Number.isInteger(kind)) {
          ;(scopes.kind ??= []).push(kind)
        }
      } else if (scope.startsWith('relay:')) {
        const relay = scope.slice('relay:'.length)
        if (relay) (scopes.relay ??= []).push(relay)
      } else if (scope.startsWith('author:')) {
        const author = scope.slice('author:'.length)
        if (author) (scopes.author ??= []).push(author)
      } else if (scope.startsWith('tag:')) {
        const value = scope.slice('tag:'.length)
        const colon = value.indexOf(':')
        const tagName = colon === -1 ? value : value.slice(0, colon)
        if (!tagName) continue

        if (colon === -1) {
          ;(scopes.tagPresence ??= []).push(tagName)
        } else {
          const tagValue = value.slice(colon + 1)
          if (!tagValue) continue
          ;(scopes.tagValue ??= {})[tagName] ??= []
          scopes.tagValue[tagName].push(tagValue)
        }
      }
    }

    next.push({
      pubkey,
      d: definition.d,
      command: definition.command!,
      title: definition.title,
      description: definition.description,
      args: definition.args.map((arg) => ({ ...arg, choices: [...arg.choices] })),
      scopes
    })

    return next
  })
}

export function uninstallCommand(pubkey: string, d: string) {
  const store = getDefaultStore()
  const key = commandKey(pubkey, d)
  store.set(installedCommandsAtom, (prev) => prev.filter((c) => commandKey(c.pubkey, c.d) !== key))
}

export function findInstalledCommands(query: string, limit = 20): TInstalledCommand[] {
  const store = getDefaultStore()
  const q = query.toLowerCase()
  return store
    .get(installedCommandsAtom)
    .filter(
      (c) =>
        !q ||
        c.command.toLowerCase().includes(q) ||
        (c.title ?? '').toLowerCase().includes(q) ||
        (c.description ?? '').toLowerCase().includes(q)
    )
    .sort((a, b) => a.command.localeCompare(b.command))
    .slice(0, limit)
}

export function findInstalledCommand(pubkey: string, d: string): TInstalledCommand | undefined {
  const store = getDefaultStore()
  const key = commandKey(pubkey, d)
  return store.get(installedCommandsAtom).find((c) => commandKey(c.pubkey, c.d) === key)
}

/** Convert an installed command back to a definition for scope helpers. */
export function installedToDefinition(command: TInstalledCommand): TCommandDefinition {
  const scopes = command.scopes
  const scopeStrings = Array.isArray(scopes)
    ? [...scopes]
    : [
        ...(scopes.kind ?? []).map((kind) => `kind:${kind}`),
        ...(scopes.relay ?? []).map((relay) => `relay:${relay}`),
        ...(scopes.author ?? []).map((author) => `author:${author}`),
        ...(scopes.tagPresence ?? []).map((tagName) => `tag:${tagName}`),
        ...Object.entries(scopes.tagValue ?? {}).flatMap(([tagName, values]) =>
          values.map((value) => `tag:${tagName}:${value}`)
        )
      ]

  return {
    d: command.d,
    command: command.command,
    title: command.title,
    description: command.description,
    args: command.args.map((arg) => ({ ...arg, choices: [...arg.choices] })),
    // Older localStorage entries stored scopes as strings or omitted them.
    scopes: scopeStrings,
    ignores: []
  }
}
